/**
 * Operator ingest surface: seven live trees, not Lead Factory CRM.
 */
import { formatInteger } from "./format.js";
import { escapeHtml, safeDateTimeLabel } from "./dashboard-html.js";
import {
  ingestFreshnessAt,
  ingestTreeStatuses,
  pipelineAttentionAlerts,
  pipelineAttentionHtml
} from "./dashboard-pipeline-alerts.js";
import { pipelineOperatorNavHtml } from "./dashboard-pipeline-nav.js";
import { fetchPropertiesConveyorHealth } from "./dashboard-properties-conveyor.js";
import type { PropertiesConveyorHealth } from "./dashboard-command-center.js";

export async function renderPipelineIngest(mount: HTMLElement): Promise<void> {
  const health = await fetchPropertiesConveyorHealth().catch(() => null);
  mount.innerHTML = pipelineIngestHtml(health);
}

export function pipelineIngestHtml(health: PropertiesConveyorHealth | null, now: Date = new Date()): string {
  const trees = ingestTreeStatuses(health, now);
  const attention = pipelineAttentionAlerts(health, now);
  const freshness = ingestFreshnessAt(health);
  const tick = health?.ingest?.lastTickAt;
  const enabled = health?.ingest?.enabled;
  const fail = health?.ingest?.lastFailureClass;
  return `
    <header class="topbar">
      <div>
        <p class="eyebrow">Seven trees</p>
        <h1>Ingest</h1>
        <p class="subtle">Live trustee lists. Close is a destination, not a source. Kania is not a tree.</p>
      </div>
    </header>
    <main class="pipeline-ingest-page" data-pipeline-ingest>
      ${pipelineOperatorNavHtml("ingest")}
      ${pipelineAttentionHtml(attention)}
      <section class="panel">
        <div class="pipeline-ingest-table-wrap">
          <table class="pipeline-ingest-table">
            <thead>
              <tr><th>Tree</th><th>Houses</th><th>Last attempt</th><th>Last seen</th><th>Status</th></tr>
            </thead>
            <tbody>
              ${trees.map((row) => `
                <tr data-ingest-tree="${row.id}"${row.stale ? ' class="is-stale"' : ""}>
                  <td>${escapeHtml(row.label)}</td>
                  <td>${row.properties == null ? "—" : formatInteger(row.properties)}</td>
                  <td>${row.lastAttemptAt ? escapeHtml(safeDateTimeLabel(row.lastAttemptAt)) : "no attempt"}</td>
                  <td>${row.lastSeenAt ? escapeHtml(safeDateTimeLabel(row.lastSeenAt)) : "not seen"}</td>
                  <td>${row.error ? escapeHtml(row.error) : row.missing ? "missing" : row.stale ? "stale" : "current"}</td>
                </tr>`).join("")}
            </tbody>
          </table>
        </div>
        ${freshness
          ? `<p class="subtle">VPS ingest evidence ${escapeHtml(safeDateTimeLabel(freshness))}${tick ? ` · scheduler ${escapeHtml(safeDateTimeLabel(tick))}` : ""}${enabled === false ? " · ingest flag off" : enabled === true ? " · ingest enabled" : ""}${fail ? ` · ${escapeHtml(fail)}` : ""}</p>`
          : `<p class="empty">Ingest freshness is unavailable.</p>`}
      </section>
    </main>
  `;
}
